/**
 * The two props that make a react-native-web `Text` a real heading
 * (FR-019(e), contracts/web-surface.md §5e).
 *
 * `role="heading"` on its own announces a heading of *unknown* depth and still
 * renders a `<div>`. `aria-level` is what supplies the depth and what makes
 * react-native-web emit an actual `<h1>`…`<h6>` — the element that document
 * outlines, "skip to next heading" navigation and the automated audit in T052
 * read. The two always travel together, which is why they are handed out
 * together rather than written by hand at each heading.
 *
 * React Native types only the `aria-*` attributes with a native equivalent, and
 * level is not one of them, so the pair has to be **spread** rather than written
 * inline: a spread is not excess-property-checked, where an inline attribute
 * would be. Holding the result in a module-level constant and spreading that —
 *
 *   const CARD_HEADING_PROPS = headingProps(2);
 *   <Text {...CARD_HEADING_PROPS}>…</Text>
 *
 * — keeps the gap in this one commented place instead of behind a cast at every
 * heading in the markup.
 *
 * The site's heading outline: the hero wordmark is the `<h1>` on `/` and the
 * not-found heading is the `<h1>` there; the feature-card headings are the
 * `<h2>`s beneath the hero. The header wordmark is deliberately a brand link
 * rather than a heading, so no page ever carries two `<h1>`s.
 */

/** Heading depths the page can render. Nothing may skip a level (FR-019(e)). */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/** The `role` / `aria-level` pair for a heading at `level`. */
export function headingProps(level: HeadingLevel) {
  return { role: "heading", "aria-level": level } as const;
}
